import type { RoleDefinition } from "../../app/roles";
import { activeIncident, scenarioEvidence } from "../../data/seed";
import { DepthBrief, PerspectiveFieldGuide, defineDepthContent, useDepth, useDepthValue } from "../../presentation";
import { useScenario } from "../../simulation/ScenarioContext";

type IncidentReportProps = {
  role: RoleDefinition;
  onOpenIncident: () => void;
};

const reportDepthContent = defineDepthContent({
  overview: {
    summary: "Review the service outcome, remaining risk, owner, and next follow-up commitment.",
    title: "Executive outcome",
    signals: ["Recovery and remaining impact", "Operational owner", "Follow-up commitment"] as const,
    executive: (decision: string) => `North Academic service was disrupted and the response team confirmed ${decision}. Recovery and remaining risk are summarized below.`,
  },
  operations: {
    summary: "Generate accountable closure records from evidence, the selected response, receipts, and handoff work.",
    title: "Closure and handoff",
    signals: ["Evidence reviewed", "Decision and recovery receipt", "Teams preview and shift handoff"] as const,
    executive: (decision: string) => `North Academic Center experienced wireless and VoIP registration degradation correlated with CHG-9012 on CR-SW-04. The response team confirmed ${decision}.`,
  },
  architecture: {
    summary: "Trace every report statement to incident, evidence, decision, receipt, asset, and maintenance identifiers.",
    title: "Report provenance",
    signals: ["INC-2048 generated-from graph", "Evidence and receipt identifiers", "Simulation and integration boundaries"] as const,
    executive: (decision: string) => `INC-2048 derives from CHG-9012, CR-SW-04, NA-214-A1, TEL-0318, linked evidence, and the ${decision} transition branch.`,
  },
});

export function IncidentReport({ role, onOpenIncident }: IncidentReportProps) {
  const { depth } = useDepth();
  const lens = useDepthValue(reportDepthContent);
  const {
    linkedEvidenceIds,
    receipts,
    recordArtifact,
    recoveryPercent,
    remainingUsers,
    selectedDecision,
    stage,
  } = useScenario();
  const reportReady = selectedDecision !== null;
  const linkedEvidence = scenarioEvidence.filter((item) => linkedEvidenceIds.includes(item.id));

  return (
    <section aria-labelledby="report-title">
      <div className="page-heading">
        <div>
          <p className="eyebrow">Generated from current local state</p>
          <h1 id="report-title">Incident report and shift handoff</h1>
          <p>{lens.summary}</p>
        </div>
        <button className="button button--secondary" onClick={onOpenIncident} type="button">
          Return to incident
        </button>
      </div>

      <DepthBrief summary={lens.summary} signals={lens.signals} title={lens.title} />
      <PerspectiveFieldGuide surface="reports" />

      {depth === "overview" && (
        <section className="depth-surface overview-action-grid" aria-label="Report outcome fields">
          <article className="overview-action-card"><span>Outcome</span><strong>{reportReady ? `${recoveryPercent}% recovered` : "Response not yet confirmed"}</strong><p>The report leads with service recovery and remaining human impact.</p></article>
          <article className="overview-action-card"><span>Accountable owner</span><strong>{activeIncident.owner}</strong><p>Ownership stays visible without requiring readers to interpret record identifiers.</p></article>
          <article className="overview-action-card"><span>Next commitment</span><strong>{reportReady ? "Monitor and complete MNT-882" : "Confirm a response branch"}</strong><p>The next handoff commitment remains explicit.</p></article>
        </section>
      )}
      {depth === "architecture" && (
        <section className="depth-surface architecture-record-grid" aria-label="Report generated-from map">
          <article className="architecture-record-card"><span>Root record</span><strong>INC-2048</strong><p>Report state is derived from the active incident and deterministic stage.</p></article>
          <article className="architecture-record-card"><span>Evidence inputs</span><strong>{linkedEvidenceIds.length || 0} linked identifiers</strong><p>EVD records retain source, observation time, and correlation meaning.</p></article>
          <article className="architecture-record-card"><span>Output boundary</span><strong>{receipts.length} receipt records</strong><p>Teams remains a non-delivered preview; report and handoff actions write local receipts.</p></article>
        </section>
      )}

      {!reportReady ? (
        <article className="panel report-empty">
          <span className="field-label">{depth === "architecture" ? "Transition gate" : "Report gate"}</span>
          <h2>
            {depth === "overview"
              ? "A recovery decision is needed before an outcome can be reported."
              : depth === "operations"
                ? "Confirm an incident response before generating closure artifacts."
                : "A decision receipt must connect INC-2048 to a recovery branch."}
          </h2>
          <p>
            {depth === "overview"
              ? "Open the incident, review the evidence, and choose the safe response. The report will then summarize the result."
              : depth === "operations"
                ? "Evidence may be collected now, but recovery and handoff statements require a selected response branch."
                : "The generated-from graph remains incomplete until a selected response creates the transition and receipt inputs."}
          </p>
          <button className="button button--primary" onClick={onOpenIncident} type="button">Open decision workspace</button>
        </article>
      ) : (
        <div className="report-layout">
          <article className="report-document">
            <header>
              <div>
                <span>Cedar Reach College · Technology Operations</span>
                <h2>{activeIncident.id} · Incident Report</h2>
              </div>
              <span className="report-state">{stage}</span>
            </header>
            <section className="report-summary">
              <div><span>Severity</span><strong>{activeIncident.severity}</strong></div>
              <div><span>Recovery</span><strong>{recoveryPercent}%</strong></div>
              <div><span>Remaining impact</span><strong>{remainingUsers} users</strong></div>
              <div><span>Owner</span><strong>{activeIncident.owner}</strong></div>
            </section>
            <section>
              <span className="field-label">Executive summary</span>
              <p>
                {lens.executive(selectedDecision?.label ?? "pending response")}
                {" "}
                {selectedDecision?.result}
              </p>
            </section>
            {depth !== "overview" && <section>
              <span className="field-label">Evidence reviewed</span>
              <ol className="report-evidence">
                {linkedEvidence.map((evidence) => (
                  <li key={evidence.id}>
                    <strong>{evidence.label}</strong>
                    <span>{evidence.source} · {evidence.observedAt}</span>
                    <p>{evidence.supports}</p>
                  </li>
                ))}
              </ol>
            </section>}
            {depth !== "architecture" && <section>
              <span className="field-label">Shift handoff</span>
              <p>
                Verify TEL-0318 and MOB-0418 registration, complete follow-up task MNT-882, and monitor CR-SW-04 for
                thirty minutes. Preserve the CHG-9012 configuration diff with the incident.
              </p>
            </section>}
            {depth === "architecture" && (
              <section>
                <span className="field-label">Generated-from relationships</span>
                <p className="mono">INC-2048 → linkedEvidenceIds[] → decisionId → receipts[] → report / handoff artifacts</p>
              </section>
            )}
            <footer>
              <span>Local demonstration artifact · Generated by {role.label}</span>
              <span>{receipts.length} supporting receipts</span>
            </footer>
          </article>

          <aside className="report-actions">
            <article className="panel teams-preview">
              <span className="preview-label">Microsoft Teams preview · Not delivered</span>
              <strong>North Academic service restoration</strong>
              <p>
                Technology Operations has {recoveryPercent === 100 ? "restored" : "improved"} network and voice
                services in North Academic Center. {remainingUsers} users remain affected. Monitoring continues under{" "}
                {activeIncident.id}.
              </p>
              <button
                className="button button--secondary"
                onClick={() => recordArtifact("teams", role.label)}
                type="button"
              >
                Record preview receipt
              </button>
            </article>
            <article className="panel artifact-stack">
              <span className="field-label">Accountable outputs</span>
              <button onClick={() => recordArtifact("report", role.label)} type="button">
                <strong>Generate report receipt</strong>
                <span>Record the current report state</span>
              </button>
              <button onClick={() => recordArtifact("handoff", role.label)} type="button">
                <strong>Generate handoff receipt</strong>
                <span>Record the next-shift summary</span>
              </button>
            </article>
          </aside>
        </div>
      )}
    </section>
  );
}
